import type { ResolvedPos, Node } from '@tiptap/pm/model';
import type { TextSelection } from '@tiptap/pm/state';
import { findParentNode } from '@tiptap/react';
import type { ChainedCommands, JSONContent, Range } from '@tiptap/react';
import type { MarkdownParser, MarkdownSerializer } from 'prosemirror-markdown';

import { isListWrapper, isNodeOfType } from '../../utils';

export interface InsertMarkdownContext {
  chain: () => ChainedCommands;
  parser: MarkdownParser;
  serializer: MarkdownSerializer;
  position: Range;
  content: string;
  selection: TextSelection;
  fromPos: ResolvedPos;
  toPos: ResolvedPos;
}

const parseContent = (parser: MarkdownParser, content: string) => {
  const doc = parser.parse(content);
  const jsonDoc = doc.toJSON() as JSONContent;

  return jsonDoc.content!;
};

export function handleHeading(context: InsertMarkdownContext): boolean | undefined {
  const { chain, content, selection, fromPos, toPos } = context;

  const heading = findParentNode(isNodeOfType('heading'))(selection);

  if (!heading) return undefined;

  const headingEnd = heading.pos + heading.node.nodeSize;
  if (toPos.pos > headingEnd) return undefined;

  const headingNode = heading.node;
  const before = headingNode.textContent.slice(0, fromPos.parentOffset);
  const after = headingNode.textContent.slice(toPos.parentOffset);

  let level = headingNode.attrs.level;
  let newContent = content;

  if (content.startsWith('#')) {
    level = (content.match(/^#+/)?.[0] || '').length;
    newContent = content.replace(/^#+\s*/, '');
  }

  return chain()
    .insertContentAt(
      {
        from: heading.pos,
        to: heading.pos + heading.node.nodeSize,
      },
      {
        type: 'heading',
        attrs: { level },
        content: [{ type: 'text', text: before + newContent + after }],
      }
    )
    .run();
}

export function handleList(context: InsertMarkdownContext): boolean | undefined {
  const { chain, parser, content, selection, position, fromPos, toPos } = context;

  const list = findParentNode(isListWrapper)(selection);

  if (!list) return undefined;

  const hasSelectedTheWholeList =
    fromPos.pos - 2 === list.start && toPos.pos + 2 >= list.start + list.node.content.size;

  if (hasSelectedTheWholeList) {
    const parsedContent = parseContent(parser, content);

    return chain()
      .deleteRange({
        from: fromPos.pos - 3,
        to: position.to,
      })
      .insertContent(parsedContent)
      .run();
  }

  if (content.trim().match(/^[-+*] /) || content.trim().match(/^\d+\. /)) {
    const lines = parseContent(parser, content)[0].content!;

    return chain()
      .insertContentAt(
        {
          from: position.from - 2,
          to: position.to,
        },
        lines
      )
      .run();
  }
}

export function handleDefaultContent(context: InsertMarkdownContext): boolean {
  const { chain, serializer, parser, content, position, fromPos, toPos } = context;

  let newContent = content;
  const newPosition = { ...position };

  if (fromPos.pos !== toPos.pos) {
    if (fromPos.parentOffset !== 0) {
      newPosition.from = Math.max(0, position.from - fromPos.parentOffset - 1);
      const parent = fromPos.parent;
      const before = parent.content.cut(0, fromPos.parentOffset);
      const serializedBefore = serializer.serialize(before as unknown as Node);
      newContent = serializedBefore + content;
    } else {
      newPosition.from = Math.max(0, position.from - 1);
    }

    if (toPos.parentOffset !== toPos.parent.content.size) {
      newPosition.to += toPos.parent.content.size - toPos.parentOffset;
      const parent = toPos.parent;
      const after = parent.content.cut(toPos.parentOffset);
      const serializedAfter = serializer.serialize(after as unknown as Node);
      newContent = newContent + serializedAfter;
    }
  }

  const parsedContent = parseContent(parser, newContent);
  return chain().insertContentAt(newPosition, parsedContent).run();
}
